import DocumentLockIcon from '@/assets/document-lock.svg?react';
import { CheckboxCell, CheckboxHeader } from '@/components/table/DataTable';
import { TanstackColumnSortIconButton } from '@/components/table/SortColumnHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useRegenerateCellService } from '@/services/tableViews';
import { isValidUrl } from '@/utils/urlUtils';
import {
  DatabaseIcon,
  getDatabaseSourceDisplayName,
} from '../../utils/databaseSources';
import type { TableViewColumnDto, TableViewRowDto } from '@ditto/shared';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Database,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ColumnModal,
  type ColumnOption,
  type ColumnType as ColumnTypeModal,
} from '../components/ColumnModal';
import { DeleteColumnConfirmModal } from '../components/DeleteColumnConfirmModal';
import { DeleteRowConfirmModal } from '../components/DeleteRowConfirmModal';
import {
  EditableDateCell,
  EditableLinkCell,
  EditableNumberCell,
  EditableOptionsCell,
  EditableTextCell,
  EditableVerbatimCell,
} from '../components/EditableCells';
import { columnTypeIcon, columnTypeTooltip } from '../utils/cellUtils';

export type ColumnType = 'text' | 'date' | 'number' | 'verbatim' | 'options';

interface UseTableViewColumnsProps {
  columns: TableViewColumnDto[];
  onUpdateColumn: (
    id: string,
    name: string,
    prompt: string,
    type: ColumnTypeModal,
    options?: ColumnOption[],
    nameOnly?: boolean
  ) => void;
  onDeleteColumn: (columnId: string) => void;
  onDeleteRow: (rowId: string) => void;
  isDeletingRow?: boolean;
  onAddColumn: (
    name: string,
    prompt: string,
    type: ColumnType,
    options?: ColumnOption[]
  ) => void;
  onMoveColumn?: (columnId: string, direction: 'left' | 'right') => void;
  setSelectedRowId: (rowId: string) => void;
  disabledAddColumn?: boolean;
  hasNoRows?: boolean;
  hasDatabaseRows?: boolean;
}

export type RowData = TableViewRowDto & {
  button?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
};

// Cell renderer component - extracted to avoid hooks in render function
const DynamicCell = ({
  row,
  column,
  columns,
  tableViewId,
  onOpenRowPanel,
}: {
  row: RowData;
  column: TableViewColumnDto;
  columns: TableViewColumnDto[];
  tableViewId: string;
  onOpenRowPanel: () => void;
}) => {
  const { regenerateCell } = useRegenerateCellService();
  const [isRegenerating, setIsRegenerating] = useState(false);

  const cell = row.cells.find(c => c.columnId === column.id);

  if (row.id.startsWith('blank-') || row.id === 'last-row') {
    return null;
  }

  if (!cell || cell.generationStatus === 'not_started') {
    return null;
  }

  if (cell.generationStatus === 'pending' || isRegenerating) {
    return (
      <FlexCol gap="2" className="w-full p-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </FlexCol>
    );
  }

  const handleRegenerate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRegenerating(true);
    regenerateCell(
      { tableViewId, data: { rowId: row.id, columnId: column.id } },
      {
        onError: () => {
          toast.error('Failed to regenerate cell');
        },
        onSettled: () => {
          setIsRegenerating(false);
        },
      }
    );
  };

  const showRegenButton =
    cell.generationStatus === 'completed' || cell.generationStatus === 'error';

  const regenButton = showRegenButton ? (
    <button
      type="button"
      onClick={handleRegenerate}
      className="absolute top-1 right-1 z-10 flex items-center justify-center w-6 h-6 rounded bg-white border border-neutral-200 shadow-sm opacity-0 group-hover/cell:opacity-100 transition-opacity hover:bg-neutral-50"
    >
      <RefreshCw className="w-3.5 h-3.5 text-neutral-900" />
    </button>
  ) : null;

  if (cell.generationStatus === 'error') {
    return (
      <div className="relative group/cell w-full h-full">
        {regenButton}
        <div
          className="cursor-pointer w-full h-full p-2"
          onClick={e => {
            e.stopPropagation();
            onOpenRowPanel();
          }}
        >
          <Text variant="body-sm" className="text-red-600 font-weight-400">
            Error extracting value
          </Text>
        </div>
      </div>
    );
  }

  if (
    cell.textValue === null &&
    cell.numberValue === null &&
    cell.dateValue === null &&
    cell.verbatimValue === null &&
    cell.selectedOptions === null &&
    cell.generationStatus === 'completed'
  ) {
    return (
      <div className="relative group/cell w-full h-full">
        {regenButton}
        <Text variant="body-sm" className="text-neutral-500 font-weight-400">
          No data found
        </Text>
      </div>
    );
  }

  const commonProps = {
    cell,
    column,
    columns,
    rowId: row.id,
    tableViewId,
    onOpenRowPanel,
  };

  const renderCell = () => {
    if (column.type === 'number' && cell.numberValue !== null) {
      return <EditableNumberCell {...commonProps} />;
    }

    if (column.type === 'date' && cell.dateValue) {
      return <EditableDateCell {...commonProps} />;
    }

    if (column.type === 'verbatim' && cell.verbatimValue) {
      return <EditableVerbatimCell {...commonProps} />;
    }

    if (column.type === 'options' && cell.selectedOptions) {
      return <EditableOptionsCell {...commonProps} />;
    }

    if (
      column.type === 'text' &&
      cell.textValue &&
      isValidUrl(cell.textValue)
    ) {
      return <EditableLinkCell {...commonProps} />;
    }

    return <EditableTextCell {...commonProps} />;
  };

  return (
    <div className="relative group/cell w-full h-full">
      {regenButton}
      {renderCell()}
    </div>
  );
};

// Item name cell component - extracted to support state for delete modal
const ItemNameCell = ({
  row,
  setSelectedRowId,
  onDeleteRow,
  isDeletingRow,
}: {
  row: RowData;
  setSelectedRowId: (rowId: string) => void;
  onDeleteRow: (rowId: string) => void;
  isDeletingRow?: boolean;
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  let displayLabel = 'Input';
  if (row.label) {
    displayLabel = row.label;
  } else if (row.fileName) {
    displayLabel = row.fileName;
  }

  if (row.id.startsWith('blank-')) {
    return '';
  }

  const handleRowClick = () => {
    if (!row.id.startsWith('blank-') && !row.button) {
      setSelectedRowId(row.id);
    }
  };

  if (row.button) {
    return (
      <FlexRow alignItems="start" gap="2" className="h-full w-full p-2">
        <Button
          variant="outline"
          size="sm"
          onClick={row.button.onClick}
          className="w-fit h-6 gap-1.5"
          disabled={row.button.disabled}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </FlexRow>
    );
  }
  const isFile = !!row.fileName;
  const isDatabase = !!row.source;

  return (
    <>
      <FlexRow
        alignItems="start"
        gap="1"
        onClick={handleRowClick}
        className="group w-full h-full cursor-pointer p-2"
      >
        <Badge
          variant="outline"
          className="max-w-[200px] min-h-[24px] gap-1.5 items-center rounded bg-white border-neutral-200 px-2 py-[3px] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]"
        >
          {isFile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0 flex items-center">
                  <DocumentLockIcon className="w-3.5 h-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent>Document</TooltipContent>
            </Tooltip>
          )}
          {isDatabase && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0 flex items-center">
                  <Database className="w-3.5 h-3.5 text-neutral-500" />
                </span>
              </TooltipTrigger>
              <TooltipContent>Database item</TooltipContent>
            </Tooltip>
          )}
          <Ellipsis asChild lineClamp={1} hasTooltip className="flex-1 min-w-0">
            <Text variant="body-xs" color="primary">
              {displayLabel}
            </Text>
          </Ellipsis>
          <button
            type="button"
            className="shrink-0 flex items-center justify-center ml-auto"
            onClick={e => {
              e.stopPropagation();
              setDeleteModalOpen(true);
            }}
          >
            <X className="w-4 h-4 text-black" />
          </button>
        </Badge>
        <ChevronRight className="w-4 h-4 text-black shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ml-auto mt-1" />
      </FlexRow>
      <DeleteRowConfirmModal
        rowName={displayLabel}
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        isDeleting={isDeletingRow}
        onConfirm={() => {
          onDeleteRow(row.id);
          setDeleteModalOpen(false);
        }}
      />
    </>
  );
};

export const useTableViewColumns = ({
  columns,
  onUpdateColumn,
  onDeleteColumn,
  onDeleteRow,
  isDeletingRow,
  onAddColumn,
  onMoveColumn,
  setSelectedRowId,
  disabledAddColumn,
  hasNoRows,
  hasDatabaseRows,
}: UseTableViewColumnsProps): ColumnDef<RowData>[] => {
  const { tableView_id: tableViewId } = useParams<{ tableView_id: string }>();

  return useMemo(() => {
    const baseColumns: ColumnDef<RowData>[] = [
      {
        accessorKey: 'counter',
        size: 45,
        minSize: 45,
        maxSize: 45,
        enableResizing: false,
        header: () => '',
        cell: ({ row }) => (
          <Text variant="body-xs" color="tertiary" className="text-center">
            {row.index + 1}
          </Text>
        ),
        meta: {
          headerClassName: 'w-[45px] min-w-[45px] max-w-[45px]',
          cellClassName:
            'w-[45px] min-w-[45px] max-w-[45px] align-top items-center',
        },
      },
      {
        accessorKey: 'rowInput',
        size: 200,
        minSize: 100,
        header: () => (
          <FlexRow alignItems="center" gap="2">
            <Text variant="body-sm" color="primary">
              Item name
            </Text>
          </FlexRow>
        ),
        cell: ({ row }) => (
          <ItemNameCell
            row={row.original}
            setSelectedRowId={setSelectedRowId}
            onDeleteRow={onDeleteRow}
            isDeletingRow={isDeletingRow}
          />
        ),
        meta: {
          headerClassName: 'border-r border-gray-200',
          cellClassName: 'align-top border-r border-gray-200 h-10 !p-0',
        },
      },
    ];

    const selectColumn: ColumnDef<RowData> = {
      id: 'select',
      size: 32,
      minSize: 32,
      enableResizing: false,
      header: ({ table }) => <CheckboxHeader table={table} />,
      cell: ({ row }) => {
        if (row.original.id.startsWith('blank-')) return '';
        if (row.original.id === 'last-row') return '';
        return <CheckboxCell row={row} />;
      },
      meta: {
        headerClassName: 'w-[32px] min-w-[32px] max-w-[32px]',
        cellClassName:
          'w-[32px] min-w-[32px] max-w-[32px] align-top items-center',
      },
    };

    const sourceColumn: ColumnDef<RowData> = {
      id: 'source',
      size: 140,
      minSize: 100,
      header: () => (
        <FlexRow alignItems="center" gap="2">
          <Database className="text-neutral-600 w-4 h-4 shrink-0" />
          <Text variant="body-sm" color="primary">
            Source
          </Text>
        </FlexRow>
      ),
      cell: ({ row }) => {
        const source = row.original.source;
        if (
          !source ||
          row.original.id.startsWith('blank-') ||
          row.original.id === 'last-row'
        ) {
          return null;
        }
        const displayName = getDatabaseSourceDisplayName(source);
        return (
          <FlexRow alignItems="center" gap="1.5" className="p-2">
            <DatabaseIcon
              sourceId={source}
              className="h-4"
              fallbackClassName="w-3.5 h-3.5 text-neutral-600"
            />
            <Text variant="body-xs" color="secondary">
              {displayName ?? source}
            </Text>
          </FlexRow>
        );
      },
      meta: {
        headerClassName: 'border-r border-gray-200',
        cellClassName: 'align-top border-r border-gray-200 h-10 !p-0',
      },
    };

    const addColumnColumn: ColumnDef<RowData> = {
      id: 'add-column',
      size: 200,
      minSize: 100,
      header: () => (
        <ColumnModal mode="create" onCreate={onAddColumn}>
          <Button
            variant="outline"
            className="w-fit h-6 gap-1.5"
            disabled={disabledAddColumn}
          >
            <Plus className="w-4 h-4 text-neutral-900" />
            <Text variant="body-xs" color="primary">
              Add column
            </Text>
          </Button>
        </ColumnModal>
      ),
      cell: () => null,
      meta: {
        headerClassName: 'border-r border-gray-200',
        cellClassName: 'align-top border-r border-gray-200',
      },
    };

    const blankColumns: ColumnDef<RowData>[] = Array.from(
      { length: 6 },
      (_, index) => ({
        id: `blank-${index}`,
        size: 200,
        minSize: 100,
        header: () => null,
        cell: () => null,
        meta: {
          headerClassName: 'border-r border-gray-200',
          cellClassName: 'align-top border-r border-gray-200',
        },
      })
    );

    if (columns.length > 0) {
      const dynamicColumns: ColumnDef<RowData>[] = columns.map(
        (column, idx) => {
          const IconComponent = columnTypeIcon(column.type);
          const tooltipText = columnTypeTooltip(column.type);

          return {
            id: column.id,
            size: 200,
            minSize: 100,
            accessorKey: 'id',
            sortingFn: (rowA, rowB) => {
              const cellA = rowA.original.cells.find(
                c => c.columnId === column.id
              );
              const cellB = rowB.original.cells.find(
                c => c.columnId === column.id
              );
              if (!cellA || !cellB) return 0;

              if (column.type === 'date') {
                return (
                  new Date(cellA.dateValue ?? '').getTime() -
                  new Date(cellB.dateValue ?? '').getTime()
                );
              }
              if (column.type === 'number') {
                return (cellA.numberValue ?? 0) - (cellB.numberValue ?? 0);
              }
              if (column.type === 'verbatim') {
                return (cellA.verbatimValue ?? '').localeCompare(
                  cellB.verbatimValue ?? ''
                );
              }
              if (column.type === 'options') {
                const firstOptionA = cellA.selectedOptions?.[0]?.value;
                const firstOptionB = cellB.selectedOptions?.[0]?.value;
                if (!firstOptionA || !firstOptionB) return 0;
                return firstOptionA.localeCompare(firstOptionB);
              }
              if (column.type === 'text') {
                return (cellA.textValue ?? '').localeCompare(
                  cellB.textValue ?? ''
                );
              }
              return 0;
            },
            header: ({ column: tableColumn }) => {
              // This will be used to mark the draggable area
              return (
                <FlexRow
                  alignItems="center"
                  gap="2"
                  justifyContent="between"
                  className="w-full"
                >
                  <FlexRow
                    alignItems="center"
                    gap="2"
                    className="flex-1 min-w-0 cursor-grab active:cursor-grabbing"
                    data-drag-handle="true"
                  >
                    <Tooltip>
                      <TooltipTrigger>
                        {IconComponent && (
                          <IconComponent className="text-neutral-600 w-4 h-4 shrink-0" />
                        )}
                      </TooltipTrigger>
                      <TooltipContent>{tooltipText}</TooltipContent>
                    </Tooltip>
                    <Ellipsis asChild lineClamp={1} hasTooltip>
                      <Text
                        variant="body-sm"
                        color="primary"
                        className="font-weight-500"
                      >
                        {column.name}
                      </Text>
                    </Ellipsis>
                  </FlexRow>
                  <FlexRow alignItems="center" gap="0" className="shrink-0">
                    {!column.isGenerating && (
                      <TanstackColumnSortIconButton column={tableColumn} />
                    )}
                    <DropdownMenu>
                      {column.isGenerating ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" disabled>
                                  <MoreVertical size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Column operations are not allowed while it is
                            generating
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                      )}
                      <DropdownMenuContent>
                        <ColumnModal
                          mode="edit"
                          id={column.id}
                          name={column.name}
                          prompt={column.prompt}
                          type={column.type}
                          options={column.options ?? undefined}
                          columns={columns}
                          onEdit={onUpdateColumn}
                        >
                          <DropdownMenuItem onSelect={e => e.preventDefault()}>
                            <Pencil size={14} />
                            Edit
                          </DropdownMenuItem>
                        </ColumnModal>
                        <DeleteColumnConfirmModal
                          columnName={column.name}
                          onConfirm={() => onDeleteColumn(column.id)}
                        >
                          <DropdownMenuItem
                            className="text-red-600"
                            onSelect={e => e.preventDefault()}
                          >
                            <Trash2 size={14} />
                            Delete
                          </DropdownMenuItem>
                        </DeleteColumnConfirmModal>
                        {onMoveColumn && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={idx === 0}
                              onSelect={() => onMoveColumn(column.id, 'left')}
                            >
                              <ArrowLeft />
                              Move left
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={idx === columns.length - 1}
                              onSelect={() => onMoveColumn(column.id, 'right')}
                            >
                              <ArrowRight />
                              Move right
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </FlexRow>
                </FlexRow>
              );
            },
            cell: ({ row }) => (
              <DynamicCell
                row={row.original}
                column={column}
                columns={columns}
                tableViewId={tableViewId ?? ''}
                onOpenRowPanel={() => setSelectedRowId(row.original.id)}
              />
            ),
            meta: {
              headerClassName: cn(
                'border-gray-200',
                idx < columns.length - 1 && 'border-r'
              ),
              cellClassName: cn(
                'align-top border-gray-200 h-10 !p-0',
                idx < columns.length - 1 && 'border-r'
              ),
            },
          };
        }
      );

      return [
        ...baseColumns,
        ...(hasDatabaseRows ? [sourceColumn] : []),
        ...(hasNoRows ? [] : [selectColumn]),
        ...dynamicColumns,
        addColumnColumn,
        ...blankColumns,
      ];
    }

    if (hasNoRows) {
      return [...baseColumns, addColumnColumn, ...blankColumns];
    }

    return [
      ...baseColumns,
      ...(hasDatabaseRows ? [sourceColumn] : []),
      selectColumn,
      addColumnColumn,
      ...blankColumns,
    ];
  }, [
    columns,
    onDeleteColumn,
    onDeleteRow,
    isDeletingRow,
    onUpdateColumn,
    onAddColumn,
    onMoveColumn,
    hasNoRows,
    hasDatabaseRows,
    disabledAddColumn,
    setSelectedRowId,
    tableViewId,
  ]);
};
